(() => {
  'use strict';

  if (window.DazumaNativeCatalogApp) {
    window.DazumaNativeCatalogApp.initAll(document);
    return;
  }

  const SECTION_SELECTOR = '[data-dnc-section]';
  const instances = new WeakMap();
  const MOBILE_MEDIA = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_MEDIA = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const parseBoolean = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  };

  const isEditableTarget = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest('input, textarea, select, [contenteditable="true"]')
    );
  };

  const createElement = (tagName, className, attributes = {}) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;

    Object.entries(attributes).forEach(([name, value]) => {
      if (value === null || value === undefined) return;
      if (name === 'text') {
        element.textContent = value;
      } else if (name === 'html') {
        element.innerHTML = value;
      } else {
        element.setAttribute(name, String(value));
      }
    });

    return element;
  };

  class DazumaNativeCatalog {
    constructor(root) {
      this.root = root;
      this.app = root.querySelector('[data-dnc-app]');
      this.config = this.readConfig();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dataAbortController = null;
      this.pages = [];
      this.slides = [];
      this.thumbnailButtons = [];
      this.productCache = new Map();
      this.currentIndex = 0;
      this.previousIndex = -1;
      this.scale = 1;
      this.minScale = 1;
      this.maxScale = 2.5;
      this.scaleStep = 0.25;
      this.isReady = false;
      this.isDestroyed = false;
      this.isThumbnailDrawerOpen = false;
      this.isProductDialogOpen = false;
      this.lastFocusedElement = null;
      this.activeHotspotButton = null;
      this.activeProductToken = 0;
      this.dragState = null;
      this.sheetDragState = null;
      this.suppressClickUntil = 0;
      this.resizeFrame = 0;
      this.openEventSent = false;

      if (!this.app || !this.config || !this.config.dataUrl) {
        return;
      }

      this.cacheElements();
      this.bindEvents();
      this.loadCatalog();
    }

    readConfig() {
      const configElement = this.root.querySelector('[data-dnc-config]');
      if (!configElement) return null;

      try {
        return JSON.parse(configElement.textContent.trim());
      } catch (error) {
        console.error('[Dazuma catalog] Invalid section configuration.', error);
        return null;
      }
    }

    cacheElements() {
      this.track = this.root.querySelector('[data-dnc-track]');
      this.viewport = this.root.querySelector('[data-dnc-viewport]');
      this.loading = this.root.querySelector('[data-dnc-loading]');
      this.errorPanel = this.root.querySelector('[data-dnc-error]');
      this.errorMessage = this.root.querySelector('[data-dnc-error-message]');
      this.retryButton = this.root.querySelector('[data-dnc-retry]');
      this.previousButton = this.root.querySelector('[data-dnc-previous]');
      this.nextButton = this.root.querySelector('[data-dnc-next]');
      this.currentPageElement = this.root.querySelector('[data-dnc-current-page]');
      this.totalPagesElement = this.root.querySelector('[data-dnc-total-pages]');
      this.zoomOutButton = this.root.querySelector('[data-dnc-zoom-out]');
      this.zoomInButton = this.root.querySelector('[data-dnc-zoom-in]');
      this.zoomResetButton = this.root.querySelector('[data-dnc-zoom-reset]');
      this.zoomValueElement = this.root.querySelector('[data-dnc-zoom-value]');
      this.fullscreenButton = this.root.querySelector('[data-dnc-fullscreen]');
      this.thumbnailOpenButton = this.root.querySelector('[data-dnc-thumbnails-open]');
      this.thumbnailCloseButton = this.root.querySelector('[data-dnc-thumbnails-close]');
      this.thumbnailDrawer = this.root.querySelector('[data-dnc-thumbnails-drawer]');
      this.thumbnailBackdrop = this.root.querySelector('[data-dnc-thumbnails-backdrop]');
      this.thumbnailGrid = this.root.querySelector('[data-dnc-thumbnail-grid]');
      this.productDialog = this.root.querySelector('[data-dnc-product-dialog]');
      this.productBackdrop = this.root.querySelector('[data-dnc-product-backdrop]');
      this.productCloseButton = this.root.querySelector('[data-dnc-product-close]');
      this.productImage = this.root.querySelector('[data-dnc-product-image]');
      this.productImagePlaceholder = this.root.querySelector('[data-dnc-product-image-placeholder]');
      this.productTitle = this.root.querySelector('[data-dnc-product-title]');
      this.productPricing = this.root.querySelector('[data-dnc-product-pricing]');
      this.productPrice = this.root.querySelector('[data-dnc-product-price]');
      this.productComparePrice = this.root.querySelector('[data-dnc-product-compare-price]');
      this.productStatus = this.root.querySelector('[data-dnc-product-status]');
      this.productPrimaryButton = this.root.querySelector('[data-dnc-product-primary]');
      this.requestStyleButton = this.root.querySelector('[data-dnc-request-style]');
      this.liveRegion = this.root.querySelector('[data-dnc-live-region]');
    }

    bindEvents() {
      const options = { signal: this.signal };

      this.previousButton?.addEventListener('click', () => this.goToPage(this.currentIndex - 1, 'previous'), options);
      this.nextButton?.addEventListener('click', () => this.goToPage(this.currentIndex + 1, 'next'), options);
      this.retryButton?.addEventListener('click', () => this.loadCatalog(), options);

      this.zoomOutButton?.addEventListener('click', () => this.setScale(this.scale - this.scaleStep), options);
      this.zoomInButton?.addEventListener('click', () => this.setScale(this.scale + this.scaleStep), options);
      this.zoomResetButton?.addEventListener('click', () => this.setScale(1), options);
      this.fullscreenButton?.addEventListener('click', () => this.toggleFullscreen(), options);

      this.thumbnailOpenButton?.addEventListener('click', () => this.openThumbnailDrawer(), options);
      this.thumbnailCloseButton?.addEventListener('click', () => this.closeThumbnailDrawer(), options);
      this.thumbnailBackdrop?.addEventListener('click', () => this.closeThumbnailDrawer(), options);

      this.productCloseButton?.addEventListener('click', () => this.closeProductDialog(), options);
      this.productBackdrop?.addEventListener('click', () => this.closeProductDialog(), options);

      this.track?.addEventListener('click', (event) => this.handleTrackClick(event), options);
      this.thumbnailGrid?.addEventListener('click', (event) => this.handleThumbnailClick(event), options);
      this.productPrimaryButton?.addEventListener('click', () => this.handleProductLinkClick(), options);

      if (parseBoolean(this.config.enableKeyboard, true)) {
        this.viewport?.addEventListener('keydown', (event) => this.handleKeyboard(event), options);
        this.root.addEventListener('keydown', (event) => this.handleGlobalKeydown(event), options);
      }

      if (parseBoolean(this.config.enableSwipe, true)) {
        this.viewport?.addEventListener('pointerdown', (event) => this.handlePointerDown(event), options);
        this.viewport?.addEventListener('pointermove', (event) => this.handlePointerMove(event), options);
        this.viewport?.addEventListener('pointerup', (event) => this.handlePointerUp(event), options);
        this.viewport?.addEventListener('pointercancel', (event) => this.handlePointerUp(event), options);
      }

      this.productDialog?.addEventListener('pointerdown', (event) => this.handleSheetPointerDown(event), options);
      this.productDialog?.addEventListener('pointermove', (event) => this.handleSheetPointerMove(event), options);
      this.productDialog?.addEventListener('pointerup', (event) => this.handleSheetPointerUp(event), options);
      this.productDialog?.addEventListener('pointercancel', (event) => this.handleSheetPointerUp(event), options);

      document.addEventListener('fullscreenchange', () => this.handleFullscreenChange(), options);
      window.addEventListener('resize', () => this.handleResize(), options);
    }

    async loadCatalog() {
      if (this.isDestroyed) return;

      this.setLoadingState(true);
      this.hideError();

      if (this.dataAbortController) {
        this.dataAbortController.abort();
      }
      this.dataAbortController = new AbortController();

      try {
        const response = await fetch(this.config.dataUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.dataAbortController.signal
        });

        if (!response.ok) {
          throw new Error(`Catalog data request failed with status ${response.status}.`);
        }

        const data = await response.json();
        this.validateData(data);
        this.pages = data.pages;
        this.catalogData = data.catalog || {};
        this.renderCatalog();
        this.isReady = true;
        this.app.setAttribute('aria-busy', 'false');
        this.root.dataset.dncReady = 'true';

        if (!this.openEventSent) {
          this.pushAnalytics('catalog_open', {
            page_order: 1,
            hotspot_id: '',
            product_handle: ''
          });
          this.pushAnalytics('catalog_page_view', {
            page_order: 1,
            hotspot_id: '',
            product_handle: '',
            navigation_source: 'initial'
          });
          this.openEventSent = true;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Dazuma catalog] Unable to load catalog data.', error);
        this.showError(error.message);
        this.setLoadingState(false);
        this.app.setAttribute('aria-busy', 'false');
      }
    }

    validateData(data) {
      if (!data || !Array.isArray(data.pages) || data.pages.length === 0) {
        throw new Error('The catalog JSON does not contain any pages.');
      }

      data.pages.forEach((page, index) => {
        if (!page || !page.image || !page.image.url) {
          throw new Error(`Catalog page ${index + 1} does not have an image URL.`);
        }

        if (!Array.isArray(page.hotspots)) {
          page.hotspots = [];
        }
      });
    }

    renderCatalog() {
      this.track.textContent = '';
      this.slides = [];
      this.thumbnailButtons = [];
      this.currentIndex = clamp(this.currentIndex, 0, this.pages.length - 1);

      const fragment = document.createDocumentFragment();

      this.pages.forEach((page, pageIndex) => {
        const slide = createElement('div', 'dnc-slide', {
          'data-dnc-slide': '',
          'data-page-index': pageIndex,
          'aria-hidden': pageIndex === this.currentIndex ? 'false' : 'true'
        });

        const canvas = createElement('div', 'dnc-page-canvas', {
          'data-dnc-page-canvas': ''
        });

        const image = createElement('img', 'dnc-page-image is-loading', {
          'data-dnc-page-image': '',
          alt: this.getPageAlt(page, pageIndex),
          width: page.image.width || 2480,
          height: page.image.height || 1754,
          decoding: 'async',
          draggable: 'false'
        });

        const hotspotLayer = createElement('div', 'dnc-hotspot-layer', {
          'data-dnc-hotspot-layer': '',
          'aria-label': `${this.getLabel('page', 'Page')} ${pageIndex + 1} product hotspots`
        });

        canvas.append(image, hotspotLayer);
        slide.append(canvas);
        fragment.append(slide);

        this.slides.push({
          page,
          slide,
          canvas,
          image,
          hotspotLayer,
          imageHydrated: false,
          hotspotHydrated: false,
          imageLoaded: false
        });
      });

      this.track.append(fragment);

      if (this.totalPagesElement) {
        this.totalPagesElement.textContent = String(this.pages.length);
      }

      this.updatePage({ source: 'initial', announce: false, force: true });
      this.hydratePage(this.currentIndex, true)
        .catch(() => null)
        .finally(() => this.setLoadingState(false));
      this.hydrateAdjacentPages(this.currentIndex);
    }

    getPageAlt(page, pageIndex) {
      const sourceName = typeof page.source_name === 'string' ? page.source_name.trim() : '';
      if (sourceName) {
        return `Dazuma lighting catalog page ${pageIndex + 1}: ${sourceName.replace(/\.[^.]+$/, '')}`;
      }
      return `Dazuma lighting catalog page ${pageIndex + 1}`;
    }

    async hydratePage(pageIndex, highPriority = false) {
      const slideState = this.slides[pageIndex];
      if (!slideState) return;

      if (!slideState.hotspotHydrated) {
        this.renderHotspots(pageIndex);
      }

      if (slideState.imageHydrated) {
        return slideState.imageLoaded;
      }

      slideState.imageHydrated = true;
      const { image, page } = slideState;
      const imageUrl = page.image.url;

      image.loading = highPriority ? 'eager' : 'lazy';
      image.fetchPriority = highPriority ? 'high' : 'low';
      image.sizes = '(min-width: 1400px) 65vw, (min-width: 750px) 94vw, 100vw';
      image.srcset = this.buildSrcset(imageUrl);
      image.src = this.withImageWidth(imageUrl, highPriority ? 1600 : 1200);

      return new Promise((resolve, reject) => {
        if (image.complete && image.naturalWidth > 0) {
          this.markImageLoaded(slideState);
          resolve(true);
          return;
        }

        const onLoad = () => {
          this.markImageLoaded(slideState);
          cleanup();
          resolve(true);
        };

        const onError = () => {
          slideState.imageHydrated = false;
          image.classList.remove('is-loading');
          cleanup();
          reject(new Error(`Unable to load catalog page ${pageIndex + 1}.`));
        };

        const cleanup = () => {
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
        };

        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onError, { once: true });
      });
    }

    markImageLoaded(slideState) {
      slideState.imageLoaded = true;
      slideState.image.classList.remove('is-loading');
      slideState.image.classList.add('is-loaded');
    }

    hydrateAdjacentPages(pageIndex) {
      [pageIndex - 1, pageIndex + 1].forEach((adjacentIndex) => {
        if (adjacentIndex < 0 || adjacentIndex >= this.pages.length) return;
        window.requestIdleCallback
          ? window.requestIdleCallback(() => this.hydratePage(adjacentIndex, false).catch(() => null), { timeout: 1200 })
          : window.setTimeout(() => this.hydratePage(adjacentIndex, false).catch(() => null), 120);
      });
    }

    renderHotspots(pageIndex) {
      const slideState = this.slides[pageIndex];
      if (!slideState || slideState.hotspotHydrated) return;

      const fragment = document.createDocumentFragment();
      const hotspots = slideState.page.hotspots || [];

      hotspots.forEach((hotspot, hotspotIndex) => {
        const product = hotspot.product || {};
        const title = product.title || 'Product';
        const button = createElement('button', 'dnc-hotspot', {
          type: 'button',
          'data-dnc-hotspot': '',
          'data-page-index': pageIndex,
          'data-hotspot-index': hotspotIndex,
          'aria-label': `${this.getLabel('productOnPage', 'View product on catalog page')} ${pageIndex + 1}: ${title}`,
          'aria-expanded': 'false',
          style: `left:${clamp(Number(hotspot.x_percent), 0, 100)}%;top:${clamp(Number(hotspot.y_percent), 0, 100)}%;`
        });

        button.append(createElement('span', '', { 'aria-hidden': 'true' }));
        fragment.append(button);
      });

      slideState.hotspotLayer.append(fragment);
      slideState.hotspotHydrated = true;
    }

    buildSrcset(url) {
      return [480, 720, 960, 1200, 1600, 2000, 2480]
        .map((width) => `${this.withImageWidth(url, width)} ${width}w`)
        .join(', ');
    }

    withImageWidth(url, width) {
      try {
        const imageUrl = new URL(url, window.location.origin);
        imageUrl.searchParams.set('width', String(width));
        return imageUrl.toString();
      } catch (error) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}width=${width}`;
      }
    }

    goToPage(nextIndex, source = 'direct') {
      if (!this.isReady || this.pages.length === 0) return;
      const clampedIndex = clamp(nextIndex, 0, this.pages.length - 1);
      if (clampedIndex === this.currentIndex && source !== 'initial') {
        this.updateTrackPosition();
        return;
      }

      this.previousIndex = this.currentIndex;
      this.currentIndex = clampedIndex;
      this.updatePage({ source, announce: true });
    }

    updatePage({ source = 'direct', announce = true, force = false } = {}) {
      if (!this.slides.length) return;

      if (!force && this.previousIndex === this.currentIndex) return;

      this.closeProductDialog(false);
      this.updateTrackPosition();
      this.updateSlideStates();
      this.updateControls();
      this.updatePageCounter();
      this.updateThumbnailState();
      this.applyScaleToActivePage();
      this.hydratePage(this.currentIndex, true).catch(() => null);
      this.hydrateAdjacentPages(this.currentIndex);

      if (announce) {
        this.announce(`${this.getLabel('page', 'Page')} ${this.currentIndex + 1} of ${this.pages.length}`);
      }

      if (source !== 'initial') {
        this.pushAnalytics('catalog_page_view', {
          page_order: this.currentIndex + 1,
          hotspot_id: '',
          product_handle: '',
          navigation_source: source
        });
      }

      if (source === 'previous') {
        this.pushAnalytics('catalog_previous', {
          page_order: this.currentIndex + 1,
          hotspot_id: '',
          product_handle: ''
        });
      } else if (source === 'next') {
        this.pushAnalytics('catalog_next', {
          page_order: this.currentIndex + 1,
          hotspot_id: '',
          product_handle: ''
        });
      }
    }

    updateTrackPosition(extraOffset = 0) {
      const basePercent = this.currentIndex * -100;
      const offsetValue = Number.isFinite(extraOffset) ? extraOffset : 0;
      this.track.style.transform = `translate3d(calc(${basePercent}% + ${offsetValue}px), 0, 0)`;
    }

    updateSlideStates() {
      this.slides.forEach((slideState, index) => {
        const isActive = index === this.currentIndex;
        slideState.slide.classList.toggle('is-active', isActive);
        slideState.slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (!isActive) {
          slideState.slide.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      });
    }

    updateControls() {
      if (this.previousButton) this.previousButton.disabled = this.currentIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.currentIndex >= this.pages.length - 1;
      if (this.zoomOutButton) this.zoomOutButton.disabled = this.scale <= this.minScale;
      if (this.zoomInButton) this.zoomInButton.disabled = this.scale >= this.maxScale;
    }

    updatePageCounter() {
      if (this.currentPageElement) {
        this.currentPageElement.textContent = String(this.currentIndex + 1);
      }
    }

    setScale(nextScale) {
      this.scale = clamp(Math.round(nextScale * 100) / 100, this.minScale, this.maxScale);
      this.applyScaleToActivePage();
      this.updateControls();

      if (this.zoomValueElement) {
        this.zoomValueElement.textContent = `${Math.round(this.scale * 100)}%`;
      }
    }

    applyScaleToActivePage() {
      this.slides.forEach((slideState, index) => {
        slideState.canvas.style.width = index === this.currentIndex ? `${this.scale * 100}%` : '100%';
      });

      const activeSlide = this.slides[this.currentIndex]?.slide;
      if (activeSlide && this.scale === 1) {
        activeSlide.scrollTo({ top: 0, left: 0, behavior: REDUCED_MOTION_MEDIA.matches ? 'auto' : 'smooth' });
      }
    }

    handleTrackClick(event) {
      if (Date.now() < this.suppressClickUntil) return;
      const button = event.target.closest('[data-dnc-hotspot]');
      if (!button || !this.track.contains(button)) return;

      const pageIndex = Number(button.dataset.pageIndex);
      const hotspotIndex = Number(button.dataset.hotspotIndex);
      const hotspot = this.pages[pageIndex]?.hotspots?.[hotspotIndex];
      if (!hotspot) return;

      this.openProductDialog(hotspot, button, pageIndex);
    }

    openProductDialog(hotspot, anchorButton, pageIndex) {
      const product = hotspot.product || {};
      const handle = product.handle || '';
      const token = ++this.activeProductToken;

      this.closeThumbnailDrawer(false);
      this.activeHotspotButton?.setAttribute('aria-expanded', 'false');
      this.activeHotspotButton = anchorButton;
      this.activeHotspotButton.setAttribute('aria-expanded', 'true');
      this.lastFocusedElement = anchorButton;
      this.isProductDialogOpen = true;

      this.renderProductFallback(product);
      this.showProductDialog(anchorButton);

      this.pushAnalytics('catalog_hotspot_click', {
        page_order: pageIndex + 1,
        hotspot_id: hotspot.id || `${pageIndex + 1}-${anchorButton.dataset.hotspotIndex}`,
        product_handle: handle
      });

      if (!handle) {
        this.renderUnavailableProduct(product);
        return;
      }

      this.fetchProduct(handle)
        .then((currentProduct) => {
          if (token !== this.activeProductToken || !this.isProductDialogOpen) return;
          this.renderCurrentProduct(currentProduct, product);
          this.positionProductDialog(anchorButton);
        })
        .catch((error) => {
          if (token !== this.activeProductToken || !this.isProductDialogOpen) return;
          console.warn(`[Dazuma catalog] Product ${handle} is unavailable.`, error);
          this.renderUnavailableProduct(product);
          this.positionProductDialog(anchorButton);
        });
    }

    renderProductFallback(product) {
      const title = product.title || 'Product';
      this.productTitle.textContent = title;
      this.productStatus.textContent = this.getLabel('loadingProduct', 'Checking current product details…');
      this.productPricing.hidden = true;
      this.productComparePrice.hidden = true;
      this.setProductImage(product.fallback_image_url || '', title);

      const fallbackUrl = product.url || this.buildProductPageUrl(product.handle);
      this.setPrimaryProductAction(fallbackUrl, this.getLabel('viewProduct', 'View product'), Boolean(fallbackUrl));

      if (this.requestStyleButton) {
        const requestUrl = this.config.requestStyleUrl || '';
        this.requestStyleButton.hidden = !requestUrl;
        if (requestUrl) this.requestStyleButton.href = requestUrl;
      }
    }

    renderCurrentProduct(currentProduct, fallbackProduct) {
      const title = currentProduct.title || fallbackProduct.title || 'Product';
      const imageUrl = this.normalizeImageUrl(currentProduct.featured_image)
        || this.normalizeImageUrl(currentProduct.images?.[0])
        || fallbackProduct.fallback_image_url
        || '';
      const productUrl = currentProduct.url || this.buildProductPageUrl(fallbackProduct.handle);
      const price = Number(currentProduct.price);
      const compareAtPrice = Number(currentProduct.compare_at_price);

      this.productTitle.textContent = title;
      this.setProductImage(imageUrl, title);

      if (Number.isFinite(price)) {
        this.productPricing.hidden = false;
        this.productPrice.textContent = this.formatMoney(price);
      } else {
        this.productPricing.hidden = true;
      }

      if (Number.isFinite(compareAtPrice) && compareAtPrice > price) {
        this.productComparePrice.hidden = false;
        this.productComparePrice.textContent = this.formatMoney(compareAtPrice);
      } else {
        this.productComparePrice.hidden = true;
        this.productComparePrice.textContent = '';
      }

      const available = Boolean(currentProduct.available);
      this.productStatus.textContent = available
        ? this.getLabel('available', 'Available online')
        : this.getLabel('soldOut', 'Currently sold out');

      this.setPrimaryProductAction(productUrl, this.getLabel('viewProduct', 'View product'), Boolean(productUrl));
    }

    renderUnavailableProduct(fallbackProduct) {
      this.productTitle.textContent = fallbackProduct.title || 'Product';
      this.productStatus.textContent = this.getLabel('unavailable', 'This exact product is no longer available.');
      this.productPricing.hidden = true;
      this.productComparePrice.hidden = true;
      this.setProductImage(fallbackProduct.fallback_image_url || '', fallbackProduct.title || 'Product');

      const similarUrl = this.config.similarOptionsUrl || this.config.requestStyleUrl || '';
      const label = this.config.similarOptionsUrl
        ? this.getLabel('viewSimilar', 'View similar options')
        : this.getLabel('requestStyle', 'Request this style');

      this.setPrimaryProductAction(similarUrl, label, Boolean(similarUrl));

      if (this.requestStyleButton) {
        const requestUrl = this.config.requestStyleUrl || '';
        this.requestStyleButton.hidden = !requestUrl || requestUrl === similarUrl;
        if (requestUrl) this.requestStyleButton.href = requestUrl;
      }
    }

    normalizeImageUrl(image) {
      if (!image) return '';
      if (typeof image === 'string') return image;
      if (typeof image === 'object') return image.src || image.url || '';
      return '';
    }

    setProductImage(url, altText) {
      this.productImage.hidden = true;
      this.productImage.removeAttribute('src');
      this.productImage.alt = altText || '';
      this.productImagePlaceholder.hidden = false;

      if (!url) {
        return;
      }

      const displayUrl = this.withImageWidth(url, 720);
      const onLoad = () => {
        this.productImagePlaceholder.hidden = true;
        this.productImage.hidden = false;
      };
      const onError = () => {
        this.productImage.hidden = true;
        this.productImagePlaceholder.hidden = false;
      };

      this.productImage.onload = onLoad;
      this.productImage.onerror = onError;
      this.productImage.src = displayUrl;
    }

    setPrimaryProductAction(url, label, enabled) {
      if (!this.productPrimaryButton) return;
      this.productPrimaryButton.textContent = label;
      this.productPrimaryButton.href = enabled ? url : '#';
      this.productPrimaryButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      this.productPrimaryButton.tabIndex = enabled ? 0 : -1;
    }

    showProductDialog(anchorButton) {
      this.productBackdrop.hidden = false;
      this.productDialog.hidden = false;
      this.productDialog.setAttribute('aria-hidden', 'false');
      this.root.classList.add('is-dialog-open');
      this.updateBodyLock();
      this.positionProductDialog(anchorButton);

      window.requestAnimationFrame(() => {
        this.productCloseButton?.focus({ preventScroll: true });
      });
    }

    positionProductDialog(anchorButton) {
      if (!this.isProductDialogOpen || !this.productDialog || !anchorButton) return;

      if (MOBILE_MEDIA.matches) {
        this.productDialog.style.left = '';
        this.productDialog.style.top = '';
        this.productDialog.style.transform = '';
        return;
      }

      window.requestAnimationFrame(() => {
        const anchorRect = anchorButton.getBoundingClientRect();
        const dialogRect = this.productDialog.getBoundingClientRect();
        const gap = 14;
        const viewportPadding = 14;

        let left = anchorRect.right + gap;
        if (left + dialogRect.width > window.innerWidth - viewportPadding) {
          left = anchorRect.left - dialogRect.width - gap;
        }
        left = clamp(left, viewportPadding, window.innerWidth - dialogRect.width - viewportPadding);

        let top = anchorRect.top + anchorRect.height / 2 - dialogRect.height / 2;
        top = clamp(top, viewportPadding, window.innerHeight - dialogRect.height - viewportPadding);

        this.productDialog.style.left = `${Math.round(left)}px`;
        this.productDialog.style.top = `${Math.round(top)}px`;
      });
    }

    closeProductDialog(restoreFocus = true) {
      if (!this.isProductDialogOpen) return;

      this.activeProductToken += 1;
      this.isProductDialogOpen = false;
      this.productDialog.hidden = true;
      this.productDialog.setAttribute('aria-hidden', 'true');
      this.productDialog.style.left = '';
      this.productDialog.style.top = '';
      this.productDialog.style.transform = '';
      this.productBackdrop.hidden = true;
      this.root.classList.remove('is-dialog-open');
      this.activeHotspotButton?.setAttribute('aria-expanded', 'false');
      this.activeHotspotButton = null;
      this.updateBodyLock();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    fetchProduct(handle) {
      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      const request = fetch(this.buildProductJsonUrl(handle), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) {
          const error = new Error(`Product request failed with status ${response.status}.`);
          error.status = response.status;
          throw error;
        }
        return response.json();
      }).catch((error) => {
        this.productCache.delete(handle);
        throw error;
      });

      this.productCache.set(handle, request);
      return request;
    }

    buildProductJsonUrl(handle) {
      return `${this.getRootUrl()}products/${encodeURIComponent(handle)}.js`;
    }

    buildProductPageUrl(handle) {
      if (!handle) return '';
      return `${this.getRootUrl()}products/${encodeURIComponent(handle)}`;
    }

    getRootUrl() {
      const shopifyRoot = window.Shopify?.routes?.root;
      const configuredRoot = this.config.rootUrl;
      const root = shopifyRoot || configuredRoot || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    formatMoney(cents) {
      const amount = Number(cents) / 100;
      const currency = this.config.currencyCode || window.Shopify?.currency?.active || 'USD';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
          maximumFractionDigits: amount % 1 === 0 ? 0 : 2
        }).format(amount);
      } catch (error) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }

    handleProductLinkClick() {
      if (!this.isProductDialogOpen || !this.activeHotspotButton) return;
      const pageIndex = Number(this.activeHotspotButton.dataset.pageIndex);
      const hotspotIndex = Number(this.activeHotspotButton.dataset.hotspotIndex);
      const hotspot = this.pages[pageIndex]?.hotspots?.[hotspotIndex];

      this.pushAnalytics('catalog_product_click', {
        page_order: pageIndex + 1,
        hotspot_id: hotspot?.id || `${pageIndex + 1}-${hotspotIndex}`,
        product_handle: hotspot?.product?.handle || ''
      });
    }

    openThumbnailDrawer() {
      if (!this.thumbnailDrawer || !parseBoolean(this.config.showThumbnails, true)) return;
      if (!this.thumbnailButtons.length) this.renderThumbnails();

      this.closeProductDialog(false);
      this.lastFocusedElement = document.activeElement;
      this.isThumbnailDrawerOpen = true;
      this.thumbnailBackdrop.hidden = false;
      this.thumbnailDrawer.hidden = false;
      this.thumbnailDrawer.setAttribute('aria-hidden', 'false');
      this.thumbnailOpenButton?.setAttribute('aria-expanded', 'true');
      this.updateThumbnailState();
      this.updateBodyLock();

      window.requestAnimationFrame(() => this.thumbnailCloseButton?.focus({ preventScroll: true }));
    }

    closeThumbnailDrawer(restoreFocus = true) {
      if (!this.isThumbnailDrawerOpen) return;
      this.isThumbnailDrawerOpen = false;
      this.thumbnailBackdrop.hidden = true;
      this.thumbnailDrawer.hidden = true;
      this.thumbnailDrawer.setAttribute('aria-hidden', 'true');
      this.thumbnailOpenButton?.setAttribute('aria-expanded', 'false');
      this.updateBodyLock();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    renderThumbnails() {
      const fragment = document.createDocumentFragment();

      this.pages.forEach((page, pageIndex) => {
        const button = createElement('button', 'dnc-thumbnail-button', {
          type: 'button',
          'data-dnc-thumbnail': '',
          'data-page-index': pageIndex,
          'aria-label': `Go to ${this.getLabel('page', 'Page')} ${pageIndex + 1}`
        });

        const image = createElement('img', 'dnc-thumbnail-image', {
          src: this.withImageWidth(page.image.url, 320),
          alt: '',
          width: 320,
          height: 226,
          loading: 'lazy',
          decoding: 'async'
        });

        const label = createElement('span', 'dnc-thumbnail-label', {
          text: `${this.getLabel('page', 'Page')} ${pageIndex + 1}`
        });

        button.append(image, label);
        fragment.append(button);
        this.thumbnailButtons.push(button);
      });

      this.thumbnailGrid.append(fragment);
    }

    handleThumbnailClick(event) {
      const button = event.target.closest('[data-dnc-thumbnail]');
      if (!button || !this.thumbnailGrid.contains(button)) return;
      const pageIndex = Number(button.dataset.pageIndex);
      this.goToPage(pageIndex, 'thumbnail');
      this.closeThumbnailDrawer(false);
      this.viewport?.focus({ preventScroll: true });
    }

    updateThumbnailState() {
      this.thumbnailButtons.forEach((button, index) => {
        const isCurrent = index === this.currentIndex;
        button.setAttribute('aria-current', isCurrent ? 'page' : 'false');
        if (isCurrent && this.isThumbnailDrawerOpen) {
          button.scrollIntoView({ block: 'nearest', behavior: REDUCED_MOTION_MEDIA.matches ? 'auto' : 'smooth' });
        }
      });
    }

    handleKeyboard(event) {
      if (!this.isReady || this.isProductDialogOpen || this.isThumbnailDrawerOpen || isEditableTarget(event.target)) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPage(this.currentIndex - 1, 'previous');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToPage(this.currentIndex + 1, 'next');
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToPage(0, 'keyboard');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToPage(this.pages.length - 1, 'keyboard');
      }
    }

    handleGlobalKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isProductDialogOpen) {
          event.preventDefault();
          this.closeProductDialog();
          return;
        }
        if (this.isThumbnailDrawerOpen) {
          event.preventDefault();
          this.closeThumbnailDrawer();
          return;
        }
      }

      if (event.key === 'Tab') {
        if (this.isProductDialogOpen) {
          this.trapFocus(event, this.productDialog);
        } else if (this.isThumbnailDrawerOpen) {
          this.trapFocus(event, this.thumbnailDrawer);
        }
      }
    }

    trapFocus(event, container) {
      const focusable = Array.from(
        container.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handlePointerDown(event) {
      if (!this.isReady || this.scale > 1 || event.button !== 0) return;
      if (event.target.closest('button, a')) return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        isHorizontal: false
      };

      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;

      if (!this.dragState.isHorizontal) {
        if (Math.abs(deltaX) < 8) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.dragState = null;
          return;
        }
        this.dragState.isHorizontal = true;
        this.track.classList.add('is-dragging');
      }

      event.preventDefault();
      this.dragState.deltaX = deltaX;

      const resistance =
        (this.currentIndex === 0 && deltaX > 0) ||
        (this.currentIndex === this.pages.length - 1 && deltaX < 0)
          ? 0.28
          : 1;

      this.updateTrackPosition(deltaX * resistance);
    }

    handlePointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const state = this.dragState;
      this.dragState = null;
      this.track.classList.remove('is-dragging');
      this.viewport.releasePointerCapture?.(event.pointerId);

      if (!state.isHorizontal) {
        this.updateTrackPosition();
        return;
      }

      this.suppressClickUntil = Date.now() + 250;
      const threshold = Math.min(90, this.viewport.clientWidth * 0.14);

      if (state.deltaX <= -threshold) {
        this.goToPage(this.currentIndex + 1, 'next');
      } else if (state.deltaX >= threshold) {
        this.goToPage(this.currentIndex - 1, 'previous');
      } else {
        this.updateTrackPosition();
      }
    }

    handleSheetPointerDown(event) {
      if (!MOBILE_MEDIA.matches || !this.isProductDialogOpen || event.button !== 0) return;
      if (event.target.closest('a, button')) return;
      if (this.productDialog.scrollTop > 0) return;

      this.sheetDragState = {
        pointerId: event.pointerId,
        startY: event.clientY,
        deltaY: 0,
        active: false
      };
      this.productDialog.setPointerCapture?.(event.pointerId);
    }

    handleSheetPointerMove(event) {
      if (!this.sheetDragState || event.pointerId !== this.sheetDragState.pointerId) return;
      const deltaY = event.clientY - this.sheetDragState.startY;
      if (deltaY <= 0) return;

      if (!this.sheetDragState.active && deltaY > 8) {
        this.sheetDragState.active = true;
      }
      if (!this.sheetDragState.active) return;

      event.preventDefault();
      this.sheetDragState.deltaY = deltaY;
      this.productDialog.style.transform = `translate3d(0, ${deltaY}px, 0)`;
    }

    handleSheetPointerUp(event) {
      if (!this.sheetDragState || event.pointerId !== this.sheetDragState.pointerId) return;
      const state = this.sheetDragState;
      this.sheetDragState = null;
      this.productDialog.releasePointerCapture?.(event.pointerId);

      if (state.active && state.deltaY > 90) {
        this.closeProductDialog();
      } else {
        this.productDialog.style.transform = '';
      }
    }

    async toggleFullscreen() {
      if (!this.root.requestFullscreen || !document.exitFullscreen) return;

      try {
        if (document.fullscreenElement === this.root) {
          await document.exitFullscreen();
        } else {
          await this.root.requestFullscreen();
          this.pushAnalytics('catalog_fullscreen', {
            page_order: this.currentIndex + 1,
            hotspot_id: '',
            product_handle: ''
          });
        }
      } catch (error) {
        console.warn('[Dazuma catalog] Fullscreen could not be opened.', error);
      }
    }

    handleFullscreenChange() {
      const isFullscreen = document.fullscreenElement === this.root;
      this.root.classList.toggle('is-fullscreen', isFullscreen);
      this.fullscreenButton?.setAttribute(
        'aria-label',
        isFullscreen ? 'Exit catalog fullscreen' : 'Open catalog in fullscreen'
      );
      this.handleResize();
    }

    handleResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        if (this.isProductDialogOpen && this.activeHotspotButton) {
          this.positionProductDialog(this.activeHotspotButton);
        }
        this.updateTrackPosition();
      });
    }

    setLoadingState(isLoading) {
      if (!this.loading) return;
      this.loading.hidden = !isLoading;
      this.app.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    showError(message) {
      if (!this.errorPanel) return;
      this.errorPanel.hidden = false;
      if (this.errorMessage && message) {
        this.errorMessage.textContent = message;
      }
    }

    hideError() {
      if (this.errorPanel) this.errorPanel.hidden = true;
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        if (this.liveRegion) this.liveRegion.textContent = message;
      }, 20);
    }

    getLabel(key, fallback) {
      return this.config.labels?.[key] || fallback;
    }

    pushAnalytics(eventName, details = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        catalog_name: this.config.catalogName || this.catalogData?.title || 'Dazuma Lighting Catalog',
        page_order: details.page_order || this.currentIndex + 1,
        hotspot_id: details.hotspot_id || '',
        product_handle: details.product_handle || '',
        device_type: MOBILE_MEDIA.matches ? 'mobile' : 'desktop',
        ...details
      });
    }

    updateBodyLock() {
      const shouldLock = this.isProductDialogOpen || this.isThumbnailDrawerOpen;
      document.body.classList.toggle('dnc-body-locked', shouldLock);
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;
      this.abortController.abort();
      this.dataAbortController?.abort();
      window.cancelAnimationFrame(this.resizeFrame);
      this.closeProductDialog(false);
      this.closeThumbnailDrawer(false);
      this.productCache.clear();
      this.root.removeAttribute('data-dnc-ready');
    }
  }

  const initSection = (root) => {
    if (!(root instanceof Element) || instances.has(root)) return;
    const instance = new DazumaNativeCatalog(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(SECTION_SELECTOR)) {
      initSection(scope);
    }
    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  };

  const destroyWithin = (scope) => {
    if (!scope) return;
    const roots = [];
    if (scope.matches?.(SECTION_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(SECTION_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const instance = instances.get(root);
      instance?.destroy();
      instances.delete(root);
    });
  };

  window.DazumaNativeCatalogApp = { initAll, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
